'use client';

import React, { useState, useRef, useEffect } from 'react';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';
import AppImage from '@/components/ui/AppImage';

interface UserContextMenuProps {
  user?: {
    name?: string;
    email: string;
    avatar?: string;
    user_metadata?: { [key: string]: any };
  };
  onLogout?: () => void;
  isAuthenticated?: boolean;
}

const UserContextMenu = ({ user, onLogout, isAuthenticated = false }: UserContextMenuProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const toggleMenu = () => {
    setIsOpen(!isOpen);
  };

  const handleLogout = () => {
    setIsOpen(false);
    onLogout?.();
  };

  if (!isAuthenticated) {
    return (
      <div className="flex items-center space-x-2">
        <Link
          href="/login"
          className="px-4 py-2 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors duration-200"
        >
          Sign In
        </Link>
        <Link
          href="/register"
          className="px-4 py-2 text-sm font-medium bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors duration-200"
        >
          Get Started
        </Link>
      </div>
    );
  }

  return (
    <div className="relative" ref={menuRef}>
      <button
        onClick={toggleMenu}
        className="flex items-center space-x-2 p-2 rounded-md hover:bg-muted transition-colors duration-200"
        aria-label="User menu"
      >
        {user?.avatar ? (
          <AppImage
            src={user.avatar}
            alt={`${user?.name || user?.user_metadata?.name} avatar`}
            className="w-8 h-8 rounded-full object-cover"
          />
        ) : (
          <div className="w-8 h-8 bg-primary rounded-full flex items-center justify-center">
            <Icon name="UserIcon" size={18} className="text-primary-foreground" />
          </div>
        )}
        <span className="hidden sm:block text-sm font-medium text-foreground">
          {user?.name || user?.user_metadata?.name || 'User'}
        </span>
        <Icon 
          name="ChevronDownIcon" 
          size={16} 
          className={`text-muted-foreground transition-transform duration-200 ${
            isOpen ? 'rotate-180' : ''
          }`} 
        />
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-2 w-56 bg-popover border border-border rounded-md shadow-soft z-50 animate-fade-in">
          <div className="p-3 border-b border-border">
            <p className="text-sm font-medium text-foreground">{user?.name || user?.user_metadata?.name || 'User'}</p>
            <p className="text-xs text-muted-foreground">{user?.email || 'user@example.com'}</p>
          </div>
          
          <div className="py-2">
            <Link
              href="/account"
              onClick={() => setIsOpen(false)}
              className="flex items-center space-x-2 px-3 py-2 text-sm text-foreground hover:bg-muted transition-colors duration-200"
            >
              <Icon name="UserIcon" size={16} />
              <span>Account</span>
            </Link>
            
            <Link
              href="/saved-plans"
              onClick={() => setIsOpen(false)}
              className="flex items-center space-x-2 px-3 py-2 text-sm text-foreground hover:bg-muted transition-colors duration-200"
            >
              <Icon name="FolderIcon" size={16} />
              <span>My Plans</span>
            </Link>
            
            <Link
              href="/account"
              onClick={() => setIsOpen(false)}
              className="flex items-center space-x-2 px-3 py-2 text-sm text-foreground hover:bg-muted transition-colors duration-200"
            >
              <Icon name="CogIcon" size={16} />
              <span>Settings</span>
            </Link>
          </div>
          
          <div className="border-t border-border py-2">
            <button
              onClick={handleLogout}
              className="flex items-center space-x-2 w-full px-3 py-2 text-sm text-error hover:bg-error/10 transition-colors duration-200"
            >
              <Icon name="ArrowRightOnRectangleIcon" size={16} />
              <span>Sign Out</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserContextMenu;